const DEFAULT_DATE_FORMAT = 'EU'

let getConfig = () => ({ dateFormat: DEFAULT_DATE_FORMAT })
let getLocale = () => {
    try {
        return localStorage.language || 'es'
    } catch (_) {
        return 'es'
    }
}
let getLocationNow = () => moment.utc()

/**
 * @param {{ configGetter?: Function, localeGetter?: Function, locationNowGetter?: Function }} options
 */
export function configureDateFormatters({ configGetter, localeGetter, locationNowGetter }) {
    if (configGetter) {
        getConfig = configGetter
    }
    if (localeGetter) {
        getLocale = localeGetter
    }
    if (locationNowGetter) {
        getLocationNow = locationNowGetter
    }
}

/**
 * @param {string} [dateFormat]
 * @returns {'US'|'EU'}
 */
export function resolveDateFormatRegion(dateFormat) {
    const fmt = String(dateFormat || DEFAULT_DATE_FORMAT).trim().toUpperCase()

    if (fmt === 'US' || fmt.includes('MM/DD')) {
        return 'US'
    }

    return 'EU'
}

/**
 * @param {string} [dateFormat]
 * @returns {boolean}
 */
export function isUsDateFormat(dateFormat) {
    return resolveDateFormatRegion(dateFormat) === 'US'
}

/**
 * @param {string} [dateFormat]
 * @returns {string}
 */
export function resolveBaseDateFormat(dateFormat) {
    return isUsDateFormat(dateFormat) ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
}

/**
 * @param {import('moment').Moment} parsed
 * @param {string} locale
 * @returns {string}
 */
export function formatWeekdayLabel(parsed, locale) {
    const label = parsed.clone().locale(locale).format('ddd').replace(/\.$/, '').trim()

    if (!label) {
        return label
    }

    return label.charAt(0).toUpperCase() + label.slice(1)
}

/**
 * Normaliza meridiano US: espacio + AM/PM en mayúsculas.
 * @param {string} formattedTime
 * @returns {string}
 */
export function normalizeUsTimeMeridiem(formattedTime) {
    return String(formattedTime).replace(/\s*(am|pm)$/i, (_, meridiem) => ` ${meridiem.toUpperCase()}`)
}

/**
 * @param {string} formattedDateTime
 * @returns {string}
 */
export function normalizeUsDateTimeMeridiem(formattedDateTime) {
    return String(formattedDateTime).replace(/(\d{1,2}:\d{2})\s*(am|pm)$/i, (_, time, meridiem) => `${time} ${meridiem.toUpperCase()}`)
}

/**
 * Sufijo "h" en etiquetas de hora EU (p. ej. 14:30h).
 * @param {string} formatted
 * @param {string} dateFormat
 * @param {boolean} [hourSuffix]
 * @returns {string}
 */
export function applyHourSuffix(formatted, dateFormat, hourSuffix = false) {
    if (!hourSuffix || !formatted || isUsDateFormat(dateFormat) || formatted.endsWith('h')) {
        return formatted
    }

    return `${formatted}h`
}

/**
 * @param {string} [dateFormat]
 * @param {{ includeTime?: boolean, includeWeekday?: boolean }} [options]
 * @returns {string}
 */
export function buildDisplayPattern(dateFormat, { includeTime = false, includeWeekday = false } = {}) {
    const baseDatePattern = resolveBaseDateFormat(dateFormat)

    let pattern = baseDatePattern

    if (includeTime) {
        pattern = isUsDateFormat(dateFormat) ? `${pattern} hh:mm A` : `${pattern} HH:mm`
    }

    return pattern
}

/**
 * @param {string} [dateFormat]
 * @param {{ includeTime?: boolean, includeWeekday?: boolean }} [options]
 * @returns {string}
 */
export function buildDisplayPatternForRelative(dateFormat, options = {}) {
    const pattern = buildDisplayPattern(dateFormat, options)

    if (!options.includeWeekday) {
        return pattern
    }

    return `ddd ${pattern}`
}

/**
 * @param {*} value
 * @returns {import('moment').Moment|null}
 */
export function parseDisplayDateValue(value) {
    if (value == null || value === '') {
        return null
    }

    if (moment.isMoment(value)) {
        return value.isValid() ? value : null
    }

    if (typeof value === 'number') {
        const ms = value < 1e12 ? value * 1000 : value
        const parsed = moment(ms)
        return parsed.isValid() ? parsed : null
    }

    const parsed = moment(value)
    return parsed.isValid() ? parsed : null
}

/**
 * @param {*} value
 * @param {Object|boolean} [options]
 * @returns {string}
 */
export function formatDisplayDate(value, options = {}) {
    const normalizedOptions = typeof options === 'boolean'
        ? { includeTime: options }
        : options

    const {
        dateFormat = DEFAULT_DATE_FORMAT,
        includeTime = false,
        includeWeekday = true,
        locale,
        emptyValue = '-',
        hourSuffix = false
    } = normalizedOptions

    const parsed = parseDisplayDateValue(value)
    if (!parsed) {
        return emptyValue
    }

    const resolvedLocale = locale != null ? locale : getLocale()
    const formatted = parsed.clone().locale(resolvedLocale).format(
        buildDisplayPattern(dateFormat, { includeTime, includeWeekday: false })
    )

    if (!includeWeekday) {
        const normalized = isUsDateFormat(dateFormat) && includeTime
            ? normalizeUsDateTimeMeridiem(formatted)
            : formatted

        return applyHourSuffix(normalized, dateFormat, includeTime && hourSuffix)
    }

    const result = `${formatWeekdayLabel(parsed, resolvedLocale)} ${formatted}`
    const normalized = isUsDateFormat(dateFormat) && includeTime
        ? normalizeUsDateTimeMeridiem(result)
        : result

    return applyHourSuffix(normalized, dateFormat, includeTime && hourSuffix)
}

/**
 * @param {*} value
 * @param {Object|boolean} [options]
 * @returns {string}
 */
export function formatDisplayDateFromStore(value, options = {}) {
    const config = getConfig() || {}
    const dateFormat = config.dateFormat || DEFAULT_DATE_FORMAT
    const locale = getLocale()

    if (typeof options === 'boolean') {
        return formatDisplayDate(value, { dateFormat, locale, includeTime: options })
    }

    return formatDisplayDate(value, {
        dateFormat,
        locale,
        ...options
    })
}

/**
 * @param {*} value
 * @param {Object} [options]
 * @returns {string}
 */
export function formatDisplayTime(value, options = {}) {
    const {
        dateFormat = DEFAULT_DATE_FORMAT,
        locale,
        emptyValue = '',
        hourSuffix = false
    } = options

    const parsed = parseDisplayDateValue(value)
    if (!parsed) {
        return emptyValue
    }

    const resolvedLocale = locale != null ? locale : getLocale()
    const formatted = parsed.clone().locale(resolvedLocale).format(
        isUsDateFormat(dateFormat) ? 'hh:mm A' : 'HH:mm'
    )

    const normalized = isUsDateFormat(dateFormat) ? normalizeUsTimeMeridiem(formatted) : formatted

    return applyHourSuffix(normalized, dateFormat, hourSuffix)
}

/**
 * @param {*} value
 * @param {Object} [options]
 * @returns {string}
 */
export function formatDisplayTimeFromStore(value, options = {}) {
    const config = getConfig() || {}
    const dateFormat = config.dateFormat || DEFAULT_DATE_FORMAT
    const locale = getLocale()

    return formatDisplayTime(value, {
        dateFormat,
        locale,
        ...options
    })
}

/**
 * @param {*} value
 * @param {Object} [options]
 * @returns {string}
 */
export function formatDisplayDateTimeFromStore(value, options = {}) {
    return formatDisplayDateFromStore(value, {
        includeTime: true,
        includeWeekday: true,
        ...options
    })
}

/**
 * @param {*} value
 * @param {Object} [options]
 * @returns {string}
 */
export function formatDisplayDateOnlyFromStore(value, options = {}) {
    return formatDisplayDateFromStore(value, {
        includeTime: false,
        includeWeekday: true,
        ...options
    })
}

/**
 * Parsea timestamps de alarmas de timers como reloj mural del local (UTC naive).
 * @param {*} value
 * @returns {import('moment').Moment|null}
 */
function parseTimerAlarmValue(value) {
    if (value == null || value === '') {
        return null
    }

    if (moment.isMoment(value)) {
        return value.isValid() ? value.clone().utc() : null
    }

    if (typeof value === 'number') {
        const ms = value < 1e12 ? value * 1000 : value
        const parsed = moment.utc(ms)
        return parsed.isValid() ? parsed : null
    }

    const parsed = moment.utc(value)
    return parsed.isValid() ? parsed : null
}

/**
 * Reloj mural del local para comparar "hoy vs otro día" en alarmas de timers.
 * @param {*} value
 * @returns {import('moment').Moment}
 */
function resolveTimerAlarmNow(value) {
    if (value != null) {
        if (moment.isMoment(value)) {
            return value.isValid() ? value.clone().utc() : moment.utc()
        }

        if (typeof value === 'number') {
            const ms = value < 1e12 ? value * 1000 : value
            return moment.utc(ms)
        }
    }

    return moment.utc(getLocationNow())
}

/**
 * Formato para alarmas de timers: solo hora si es hoy, fecha+hora si es otro día.
 * Usa reloj mural del local, no la zona horaria del dispositivo.
 * @param {*} value
 * @param {Object} [options]
 * @param {*} [options.now] - instante de referencia del local (timestamp o moment UTC naive)
 * @returns {string}
 */
export function formatTimerAlarmFromStore(value, options = {}) {
    const { hourSuffix = true, ...restOptions } = options
    const parsed = parseTimerAlarmValue(value)
    if (!parsed) {
        return restOptions.emptyValue != null ? restOptions.emptyValue : ''
    }

    const alarm = parsed.clone()
    const now = resolveTimerAlarmNow(restOptions.now)
    const formatterOptions = { ...restOptions, hourSuffix }

    if (alarm.isSame(now, 'day')) {
        return formatDisplayTimeFromStore(alarm, formatterOptions)
    }

    return formatDisplayDateTimeFromStore(alarm, formatterOptions)
}
